import React, { useState } from 'react';
import { App, Button, Card, Col, Descriptions, Form, Input, Row, Select, Space, Spin, Table, Tag } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { formatDate } from '@/utils/format';

const FIELD_OPTIONS = [
  { value: 'personal_email', label: 'Personal email' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'address', label: 'Address' },
  { value: 'bank_name', label: 'Bank name' },
  { value: 'bank_account_number', label: 'Bank account number' },
  { value: 'iban', label: 'IBAN' },
  { value: 'bank_branch_name', label: 'Bank branch' },
];

const EssProfilePage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [fieldKey, setFieldKey] = useState<string>('mobile');

  const { data: profile, isLoading } = useQuery({
    queryKey: hrmsKeys.essProfile(),
    queryFn: () => hrmsService.getEssProfile(),
  });
  const { data: requests = [] } = useQuery({
    queryKey: hrmsKeys.essProfileChanges(),
    queryFn: () => hrmsService.listEssProfileChangeRequests(),
  });

  const createMut = useMutation({
    mutationFn: (body: object) => hrmsService.createEssProfileChangeRequest(body),
    onSuccess: () => {
      message.success('Change request submitted for approval');
      form.resetFields(['new_value']);
      qc.invalidateQueries({ queryKey: hrmsKeys.essProfileChanges() });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Request failed'),
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="My profile"
          subtitle="View your details and request changes that need HR approval"
        />
      }
    >
      {isLoading ? (
        <Spin />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Current details" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Employee">{profile?.employee_code} — {profile?.full_name}</Descriptions.Item>
                <Descriptions.Item label="Job title">{profile?.job_title || '—'}</Descriptions.Item>
                <Descriptions.Item label="Department">{profile?.department_name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Location">{profile?.location_name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Work email">{profile?.company_email || '—'}</Descriptions.Item>
                <Descriptions.Item label="Personal email">{profile?.personal_email || '—'}</Descriptions.Item>
                <Descriptions.Item label="Mobile">{profile?.mobile || '—'}</Descriptions.Item>
                <Descriptions.Item label="Address">{profile?.address || '—'}</Descriptions.Item>
                <Descriptions.Item label="Bank">{profile?.bank_name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Account">{profile?.bank_account_number || '—'}</Descriptions.Item>
                <Descriptions.Item label="IBAN">{profile?.iban || '—'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Request a change" size="small">
              <Form
                form={form}
                layout="vertical"
                onFinish={(v) =>
                  createMut.mutate({
                    field_key: v.field_key,
                    new_value: v.new_value,
                    old_value: profile?.[v.field_key] ?? null,
                  })
                }
                initialValues={{ field_key: 'mobile' }}
              >
                <Form.Item name="field_key" label="Field" rules={[{ required: true }]}>
                  <Select
                    options={FIELD_OPTIONS}
                    onChange={(v) => {
                      setFieldKey(v);
                      form.setFieldValue('new_value', profile?.[v] ?? '');
                    }}
                  />
                </Form.Item>
                <Form.Item name="new_value" label="New value" rules={[{ required: true, message: 'Enter the new value' }]}>
                  {fieldKey === 'address' ? <Input.TextArea rows={3} /> : <Input />}
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={createMut.isPending}>
                  Submit for approval
                </Button>
              </Form>
            </Card>
            <Card title="My change requests" size="small" style={{ marginTop: 16 }}>
              <Table
                rowKey="id"
                size="small"
                pagination={{ pageSize: 5 }}
                dataSource={requests}
                columns={[
                  { title: 'Field', dataIndex: 'field_key' },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
                  },
                  {
                    title: 'Requested',
                    dataIndex: 'created_at',
                    render: (v: string) => (v ? formatDate(v) : '—'),
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      )}
      <Space style={{ marginTop: 16 }}>
        <Button href="/hr/me">Back to My HRMS</Button>
      </Space>
    </WorkspaceLayout>
  );
};

export default EssProfilePage;
